import useGetProfileInfo from 'domain/profile/hooks/useGetProfileInfo';
import useGetProfileAvatar from 'domain/profile/hooks/useGetProfileAvatar';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { DocumentLink } from 'domain/documents/schemas/document';
import FavoriteList from './FavoriteList';
import { CircleQuestionMark, LogOut, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { useAuthContext } from 'domain/auth/hooks/useAuthContext';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

type Props = {
  documents?: DocumentLink[];
  isLoading?: boolean;
  isMobileDisplay?: boolean;
  onProfileClick?: () => void;
  toggleMobileMenu?: () => void;
};

export default function SettingsMenu({ documents, isLoading }: Props) {
  const { data: user, isLoading: isLoadingProfile } = useGetProfileInfo();
  const { data, isLoading: isLoadingAvatarPhoto } = useGetProfileAvatar();
  const { signOut } = useAuthContext();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  const baoSupportEmail = 'bao-support@gcloutier.com';
  const hrefPrefix = 'mailto:' + baoSupportEmail + '?subject=';

  const initials = user ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}` : '';

  function profileClick() {
    navigate('/profile');
  }

  return (
    <div className="ml-auto flex h-16 items-center justify-between gap-4 bg-primary p-1">
      <div className="flex items-center gap-4">
        <div className="whitespace-nowrap text-white hover:underline">
          {isLoadingProfile || isLoadingAvatarPhoto ? (
            <div className="flex items-center justify-center">
              <LoadingSpinner isMini isWhite={true} />
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="cursor-pointer">
                <div className="flex items-center gap-2">
                  <Avatar className="h-11 w-11 rounded-full">
                    <AvatarImage
                      className="h-full w-full rounded-full object-cover"
                      src={data ? `data:${data.mimeType};base64,${data.contentAsBase64}` : undefined}
                      alt="Avatar Photo"
                    />
                    <AvatarFallback className="bg-secondaryYellow text-sm font-semibold text-primary">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <p className="hidden text-sm text-white lg:inline-block">{`${user?.firstName} ${user?.lastName}`}</p>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="mr-5">
                <DropdownMenuLabel>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-20 w-20 rounded-full">
                      <AvatarImage
                        className="h-full w-full rounded-full object-cover"
                        src={data ? `data:${data.mimeType};base64,${data.contentAsBase64}` : undefined}
                        alt="Avatar Photo"
                      />
                      <AvatarFallback className="bg-secondaryYellow text-xl font-semibold text-primary">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-lg text-secondaryBlack">{`${user?.firstName} ${user?.lastName}`}</p>
                      <p className="overflow-x-hidden text-ellipsis whitespace-nowrap text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <FavoriteList documents={documents} isLoading={isLoading} />
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem onSelect={profileClick} className="text-md">
                    <User />
                    {t('profile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-md">
                    <a href={hrefPrefix + t('assistance-email-subject')} className="flex items-center gap-2">
                      <CircleQuestionMark />
                      {t('help')}
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={signOut} className="text-md">
                    <LogOut />
                    {t('logOut')}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}
